import { authStore } from '~/stores/auth';
import { useTracking } from '~/composables/useTracking';

export interface FavoriteableExpert {
  id: string;
  handle?: string;
  first_name: string;
  last_name: string;
  title?: string;
  signed_picture_url?: string;
  validated_feedback_count?: number;
}

const LEGACY_COOKIE_NAME = 'favoriteExperts';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export interface FavoriteExpert {
  id: string;
  handle?: string;
  first_name: string;
  last_name: string;
  title?: string;
  signed_picture_url?: string;
  validated_feedback_count: number;
  added_at: string;
}

// Scope cookie per userId to isolate favorites across impersonation sessions
function getCookieName(): string {
  try {
    const userId = authStore().user?.id;
    if (userId) {
      return `${LEGACY_COOKIE_NAME}:${userId}`;
    }
  } catch {
    // authStore may not be available during SSR or early init
  }
  return LEGACY_COOKIE_NAME;
}

export function useFavoriteExperts() {
  const cookieName = getCookieName();

  const cookie = useCookie<FavoriteExpert[]>(cookieName, {
    default: () => [],
    maxAge: COOKIE_MAX_AGE,
    watch: true,
  });

  // One-time migration: copy legacy cookie data into the scoped cookie
  if (cookieName !== LEGACY_COOKIE_NAME) {
    const legacyCookie = useCookie<FavoriteExpert[] | null>(LEGACY_COOKIE_NAME);
    if ((!cookie.value || cookie.value.length === 0) && legacyCookie.value && legacyCookie.value.length > 0) {
      cookie.value = [...legacyCookie.value];
      legacyCookie.value = null;
    }
  }

  const { trackEvent } = useTracking();

  const favorites = computed(() => cookie.value || []);

  const favoriteIds = computed(() => new Set(favorites.value.map(f => f.id)));

  const isFavorite = (expertId: string): boolean => {
    return favoriteIds.value.has(expertId);
  };

  const addFavorite = (expert: FavoriteableExpert): void => {
    if (isFavorite(expert.id)) return;

    const favoriteExpert: FavoriteExpert = {
      id: expert.id,
      handle: expert.handle,
      first_name: expert.first_name,
      last_name: expert.last_name,
      title: expert.title,
      signed_picture_url: expert.signed_picture_url,
      validated_feedback_count: expert.validated_feedback_count ?? 0,
      added_at: new Date().toISOString(),
    };

    cookie.value = [...(cookie.value || []), favoriteExpert];
    trackEvent("shortlist", "expert_shortlisted");
  };

  const removeFavorite = (expertId: string): void => {
    cookie.value = (cookie.value || []).filter(f => f.id !== expertId);
  };

  const toggleFavorite = (expert: FavoriteableExpert): void => {
    if (isFavorite(expert.id)) {
      removeFavorite(expert.id);
    } else {
      addFavorite(expert);
    }
  };

  const clearFavorites = (): void => {
    cookie.value = [];
  };

  const favoritesCount = computed(() => favorites.value.length);

  return {
    favorites,
    favoriteIds,
    favoritesCount,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    clearFavorites,
  };
}
